import { inject, Injectable } from '@angular/core';
import { Survey, SyncChoice } from '../models/survey.model';
import { FirebaseSurveyService } from './firebase-survey.service';
import { IdbSurveyService } from './idb-survey.service';
import { countSurvey, SurveyCounts, SurveyLogService } from './survey-log.service';
import { SurveySnapshotService } from './survey-snapshot.service';

export type SurveySyncIndicator = 'none' | 'newer-online' | 'kept-local';

export interface SurveySyncComparison {
  local: SurveyCounts;
  remote: SurveyCounts;
}

/** Material icons for each indicator. Not the cloud, which already means "saved online". */
export const SYNC_INDICATOR_ICONS: Record<SurveySyncIndicator, string> = {
  none: '',
  'newer-online': 'sync_problem',
  'kept-local': 'difference',
};

export interface RemoteCheck {
  remoteUpdatedAt: number | undefined;
  pending: Survey | null;
}

type LocalSyncView = Pick<Survey, 'updatedAt' | 'lastPublishedDate' | 'syncState'>;

/**
 * Decides which sync indicator a survey card/page shows, given the local copy and the
 * `updatedAt` of the online copy.
 */
export function resolveSyncIndicator(local: LocalSyncView | undefined, remoteUpdatedAt: number | undefined): SurveySyncIndicator {
  if (!local || remoteUpdatedAt === undefined) {
    return 'none';
  }

  if (local.syncState?.choice === 'local' && local.syncState.remoteUpdatedAt === remoteUpdatedAt) {
    return 'kept-local';
  }

  if (local.lastPublishedDate !== undefined && remoteUpdatedAt > local.updatedAt) {
    return 'newer-online';
  }

  return 'none';
}

const UNSYNCED_TOLERANCE_MS = 1000;

export function hasUnsyncedChanges(survey: Pick<Survey, 'updatedAt' | 'lastPublishedDate'> | null | undefined): boolean {
  if (!survey?.lastPublishedDate) {
    return false;
  }
  return survey.updatedAt - survey.lastPublishedDate > UNSYNCED_TOLERANCE_MS;
}

@Injectable({ providedIn: 'root' })
export class SurveySyncService {
  private readonly idbSurveyService = inject(IdbSurveyService);
  private readonly firebaseSurveyService = inject(FirebaseSurveyService);
  private readonly snapshots = inject(SurveySnapshotService);
  private readonly surveyLog = inject(SurveyLogService);

  /**
   * Looks at the online copy when a survey is opened. `remoteUpdatedAt` is the online version's timestamp
   * (undefined when unreachable) and `pending` is the online copy to offer, if a decision is still needed.
   */
  async checkRemote(local: Survey): Promise<RemoteCheck> {
    const remote = await this.firebaseSurveyService.fetchRemoteSurvey(local.id);
    if (!remote) {
      return { remoteUpdatedAt: undefined, pending: null };
    }

    const pending = resolveSyncIndicator(local, remote.updatedAt) === 'newer-online' ? remote : null;
    return { remoteUpdatedAt: remote.updatedAt, pending };
  }

  /** The online copy for a user-initiated review (clicking the sync icon). Null if unreachable. */
  async findRemoteForReview(local: Survey): Promise<Survey | null> {
    const remote = await this.firebaseSurveyService.fetchRemoteSurvey(local.id);
    if (!remote) {
      return null;
    }
    return remote.updatedAt === local.updatedAt ? null : remote;
  }

  compare(local: Survey, remote: Survey): SurveySyncComparison {
    return { local: countSurvey(local), remote: countSurvey(remote) };
  }

  async applyChoice(local: Survey, remote: Survey, choice: SyncChoice): Promise<Survey> {
    return this.surveyLog.track('survey.sync_choice', { surveyId: local.id, source: 'idb', choice, before: countSurvey(local) }, async (tracked) => {
      const applied = choice === 'remote' ? await this.applyRemote(local, remote) : await this.keepLocal(local, remote);
      tracked.after = countSurvey(applied);
      return applied;
    });
  }

  private async applyRemote(local: Survey, remote: Survey): Promise<Survey> {
    await this.snapshots.take(local, 'load-remote');
    const { syncState: _discarded, ...remoteWithoutSyncState } = remote;
    await this.idbSurveyService.updateSurvey(remoteWithoutSyncState, { preserveUpdatedAt: true });
    return remoteWithoutSyncState;
  }

  private async keepLocal(local: Survey, remote: Survey): Promise<Survey> {
    const kept: Survey = { ...local, syncState: { remoteUpdatedAt: remote.updatedAt, choice: 'local' } };
    await this.idbSurveyService.updateSurvey(kept, { preserveUpdatedAt: true });
    return kept;
  }
}
